import { getLoginAD } from '@/core/configs/auth/auth.utils';
import { Usuario } from '@/modules/auth/models/usuario.model';
import { FiltroMensagem } from '@/core/models/mensagens/filtro-mensagem.model';
import { ArquivoAnexo, Mensagem } from '@/core/models/mensagens/mensagem.model';
import { normalizePagedList, PagedList } from '@/core/models/paged-list.model';
import HttpCotinService from './HttpCotinService';

const CONTROLLER = 'BFFProxy/Mensagem';

const inflightFilterRequests = new Map<string, Promise<PagedList<Mensagem>>>();

export const MENSAGEM_DEFAULT_SYSTEM_NAME = 'TemplateCotinReact';

export function mapearAnexosParaMensagemDtoFiles(
    arquivos: ArquivoAnexo[]
): { nomeArquivo: string; documentoID?: number; size: number }[] {
    return arquivos.map((a) => ({
        nomeArquivo: a.nomeArquivo,
        documentoID: a.documentoID != null && a.documentoID > 0 ? a.documentoID : undefined,
        size: a.tamanho ?? 0,
    }));
}

export function mensagemParaDtoPut(
    m: Mensagem,
    patch: Record<string, unknown> = {}
): Record<string, unknown> {
    const sistemaStr =
        (m as Mensagem & { sistema?: string }).sistema ?? m.deSistema ?? '';
    const files =
        Array.isArray(m.arquivos) && m.arquivos.length > 0
            ? mapearAnexosParaMensagemDtoFiles(m.arquivos)
            : [];
    const base: Record<string, unknown> = {
        id: m.id,
        historyID: m.historyID ?? '',
        parameterSistemaID: m.parameterSistemaID ?? '',
        sistema: sistemaStr,
        deUsuario: m.deUsuario ?? '',
        paraUsuario: m.paraUsuario ?? '',
        perfil: m.perfil ?? '',
        assunto: m.assunto ?? '',
        mensagem: m.mensagem ?? '',
        dataInclusao: m.dataInclusao ?? null,
        dataEnvio: m.dataEnvio ?? null,
        dataLeitura: m.dataLeitura ?? null,
        tipoMensagensID: m.tipoMensagensID ?? 1,
        mensagensParametros: m.mensagensParametros ?? '',
        mensagemAlerta: m.mensagemAlerta ?? null,
        statusMensagem: m.statusMensagem ?? null,
        apresentacaoApp: m.apresentacaoApp ?? false,
        emailsAdicionais: m.emailsAdicionais ?? '',
        systemName: MENSAGEM_DEFAULT_SYSTEM_NAME,
        sendMessageQueue: false,
        files,
    };
    return { ...base, ...patch };
}

function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result as string;
            const base64 = result.split(',')[1] ?? result;
            resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

type MensagemApiDto = Record<string, unknown>;

const listeners = new Set<() => void>();

export function subscribeAtualizacaoNotificacoes(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

export function solicitarAtualizacaoNotificacoes(): void {
    listeners.forEach((listener) => {
        try {
            listener();
        } catch {
            // ignore listener errors
        }
    });
}

export const FILTRO_MENSAGENS_NOTIFICACAO: FiltroMensagem = {
    pageNo: 1,
    pageSize: 1000,
    mensagemAlerta: true,
};

const NOTIFICATION_POLL_INTERVAL_MS = 15000;
const NOTIFICATION_VISIBILITY_THROTTLE_MS = 600;

export type MensagensNotificationPollingOptions = {
    loginAD: string;
    enabled?: boolean;
    intervalMs?: number;
    onData: (data: PagedList<Mensagem>) => void;
    onError?: () => void;
};

/**
 * Polling do sino de notificações com fila serializada (equivalente ao merge + switchMap do Angular).
 * Evita POST duplicado no mount, visibilitychange e solicitarAtualizacaoNotificacoes.
 */
export function startMensagensNotificationPolling(
    options: MensagensNotificationPollingOptions
): () => void {
    const {
        loginAD,
        enabled = true,
        intervalMs = NOTIFICATION_POLL_INTERVAL_MS,
        onData,
        onError,
    } = options;

    const normalizedLoginAD = loginAD.trim();
    if (!enabled || !normalizedLoginAD) {
        return () => undefined;
    }

    let disposed = false;
    let fetchChain: Promise<void> = Promise.resolve();
    let visibilityTimer: number | undefined;

    const usuarioMinimo = { loginAD: normalizedLoginAD } as Usuario;

    const enqueueFetch = () => {
        if (disposed) {
            return;
        }

        fetchChain = fetchChain
            .then(async () => {
                if (disposed) {
                    return;
                }
                try {
                    const data = await postMensagensFilter(FILTRO_MENSAGENS_NOTIFICACAO, usuarioMinimo);
                    if (!disposed) {
                        onData(data);
                    }
                } catch {
                    if (!disposed) {
                        onError?.();
                    }
                }
            })
            .catch(() => undefined);
    };

    enqueueFetch();

    const intervalId = window.setInterval(enqueueFetch, intervalMs);
    const unsubscribeRefresh = subscribeAtualizacaoNotificacoes(enqueueFetch);

    const onVisibility = () => {
        if (disposed || document.visibilityState !== 'visible') {
            return;
        }
        if (visibilityTimer) {
            clearTimeout(visibilityTimer);
        }
        visibilityTimer = window.setTimeout(enqueueFetch, NOTIFICATION_VISIBILITY_THROTTLE_MS);
    };

    document.addEventListener('visibilitychange', onVisibility);

    return () => {
        disposed = true;
        window.clearInterval(intervalId);
        unsubscribeRefresh();
        document.removeEventListener('visibilitychange', onVisibility);
        if (visibilityTimer) {
            clearTimeout(visibilityTimer);
        }
    };
}

function toDate(value: unknown): Date | undefined {
    if (value == null) {
        return undefined;
    }
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? undefined : value;
    }
    const parsed = new Date(String(value));
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

function mapearFilesApiParaArquivosAnexos(files: unknown): ArquivoAnexo[] {
    if (!Array.isArray(files) || files.length === 0) {
        return [];
    }

    return files.map((raw) => {
        const f = (raw ?? {}) as Record<string, unknown>;
        const nome =
            (typeof f.nomeArquivo === 'string' && f.nomeArquivo) ||
            (typeof f.NomeArquivo === 'string' && f.NomeArquivo) ||
            '';
        const documentoIDRaw = f.documentoID ?? f.DocumentoID ?? f.documentID ?? f.DocumentID;
        const documentoID =
            typeof documentoIDRaw === 'number'
                ? documentoIDRaw
                : typeof documentoIDRaw === 'string' && /^\d+$/.test(documentoIDRaw)
                  ? Number(documentoIDRaw)
                  : undefined;
        const tamanhoRaw = f.size ?? f.Size;
        const tamanho =
            typeof tamanhoRaw === 'number'
                ? tamanhoRaw
                : typeof tamanhoRaw === 'string' && /^\d+$/.test(tamanhoRaw)
                  ? Number(tamanhoRaw)
                  : undefined;

        return {
            nomeArquivo: nome,
            documentoID,
            tamanho,
            url: typeof (f.url ?? f.Url) === 'string' ? String(f.url ?? f.Url) : undefined,
        };
    });
}

function mapItemApiParaMensagem(item: MensagemApiDto): Mensagem {
    const arquivos = mapearFilesApiParaArquivosAnexos(item.files ?? item.Files);
    return {
        id: Number(item.id ?? item.ID ?? 0),
        historyID: String(item.historyID ?? item.HistoryID ?? ''),
        parameterSistemaID: String(item.parameterSistemaID ?? item.ParameterSistemaID ?? ''),
        deSistema: String(item.deSistema ?? item.Sistema ?? ''),
        paraSistema: '',
        deUsuario: String(item.deUsuario ?? item.DeUsuario ?? ''),
        paraUsuario: String(item.paraUsuario ?? item.ParaUsuario ?? ''),
        assunto: String(item.assunto ?? item.Assunto ?? ''),
        mensagem: String(item.mensagem ?? item.Mensagem ?? ''),
        arquivoAnexo: null,
        arquivoAnexoUrl: null,
        dataInclusao: toDate(item.dataInclusao ?? item.DataInclusao),
        dataEnvio: toDate(item.dataEnvio ?? item.DataEnvio),
        dataLeitura: toDate(item.dataLeitura ?? item.DataLeitura),
        tipoMensagensID:
            typeof (item.tipoMensagensID ?? item.TipoMensagensID) === 'number'
                ? Number(item.tipoMensagensID ?? item.TipoMensagensID)
                : undefined,
        mensagensParametros: String(item.mensagensParametros ?? item.MensagensParametros ?? ''),
        mensagemAlerta:
            typeof (item.mensagemAlerta ?? item.MensagemAlerta) === 'boolean'
                ? Boolean(item.mensagemAlerta ?? item.MensagemAlerta)
                : undefined,
        statusMensagem:
            typeof (item.messageStatus ?? item.MessageStatus) === 'boolean'
                ? Boolean(item.messageStatus ?? item.MessageStatus)
                : undefined,
        qtdDiasResposta:
            typeof (item.qtdDiasResposta ?? item.QtdDiasResposta) === 'number'
                ? Number(item.qtdDiasResposta ?? item.QtdDiasResposta)
                : undefined,
        perfil: String(item.perfil ?? item.Perfil ?? ''),
        emailsAdicionais: String(item.emailsAdicionais ?? item.EmailsAdicionais ?? ''),
        apresentacaoApp:
            typeof (item.apresentacaoApp ?? item.ApresentacaoApp) === 'boolean'
                ? Boolean(item.apresentacaoApp ?? item.ApresentacaoApp)
                : undefined,
        arquivos: arquivos.length > 0 ? arquivos : undefined,
    };
}

function mapFiltroParaApi(
    filtro: FiltroMensagem,
    loginADOrUser?: string | Usuario | null
): Record<string, unknown> {
    const loginAD =
        typeof loginADOrUser === 'string'
            ? loginADOrUser.trim()
            : getLoginAD(loginADOrUser ?? null);
    const paraUsuario = loginAD || filtro.paraUsuario;
    return {
        pageNo: filtro.pageNo ?? 1,
        pageSize: filtro.pageSize ?? 10,
        orderByColumn: filtro.orderByColumn,
        isAsc: filtro.isAsc,
        paraUsuario,
        deUsuario: filtro.deUsuario,
        assunto: filtro.assunto,
        dataEnvio: filtro.dataEnvio,
        dataLeitura: filtro.dataLeitura,
        tipoMensagensID: filtro.tipoMensagensID,
        mensagensParametros: filtro.mensagensParametros,
        mensagemAlerta: filtro.mensagemAlerta,
        qtdDiasResposta: filtro.qtdDiasResposta,
        historyID: filtro.historyID,
        id: filtro.id,
    };
}

export function buildMensagensFilterRequestKey(
    filtro: FiltroMensagem,
    loginAD?: string | null
): string {
    return JSON.stringify(mapFiltroParaApi(filtro, loginAD ?? undefined));
}

function buildMensagensFilterKey(filtro: FiltroMensagem, usuario?: Usuario | null): string {
    return buildMensagensFilterRequestKey(filtro, getLoginAD(usuario));
}

const RECENT_FILTER_TTL_MS = 2500;
const recentFilterRequests = new Map<string, { promise: Promise<PagedList<Mensagem>>; expiresAt: number }>();

async function executePostMensagensFilter(
    filtro: FiltroMensagem,
    usuario?: Usuario | null
): Promise<PagedList<Mensagem>> {
    const body = mapFiltroParaApi(filtro, usuario);
    const data = await HttpCotinService.Post<unknown>(`${CONTROLLER}/PostMensagensFilter`, body);
    const normalized = normalizePagedList<MensagemApiDto>(data);

    return {
        ...normalized,
        items: (normalized.items ?? []).map((item) => mapItemApiParaMensagem(item)),
    };
}

export async function postMensagensFilter(
    filtro: FiltroMensagem,
    usuario?: Usuario | null
): Promise<PagedList<Mensagem>> {
    const key = buildMensagensFilterKey(filtro, usuario);

    if (!inflightFilterRequests.has(key)) {
        const recent = recentFilterRequests.get(key);
        if (recent && recent.expiresAt > Date.now()) {
            return recent.promise;
        }

        const request = executePostMensagensFilter(filtro, usuario).finally(() => {
            if (inflightFilterRequests.get(key) === request) {
                inflightFilterRequests.delete(key);
            }
        });

        inflightFilterRequests.set(key, request);
        recentFilterRequests.set(key, {
            promise: request,
            expiresAt: Date.now() + RECENT_FILTER_TTL_MS,
        });
    }

    return inflightFilterRequests.get(key)!;
}

export async function postMensagem(payload: Record<string, unknown>): Promise<unknown> {
    return HttpCotinService.Post<unknown>(CONTROLLER, payload);
}

export async function putMensagem(payload: Record<string, unknown>): Promise<unknown> {
    return HttpCotinService.Put<unknown>(CONTROLLER, payload);
}

export async function getMensagemById(id: number, usuario?: Usuario | null): Promise<Mensagem | null> {
    const response = await postMensagensFilter(
        {
            id,
            pageNo: 1,
            pageSize: 1,
        },
        usuario
    );
    return response.items?.[0] ?? null;
}

export async function getHistoricoMensagem(
    historyID: string,
    usuario?: Usuario | null
): Promise<PagedList<Mensagem>> {
    return postMensagensFilter(
        {
            historyID,
            pageNo: 1,
            pageSize: 100,
            orderByColumn: 'DataEnvio',
            isAsc: true,
        },
        usuario
    );
}

export async function arquivarMensagem(id: number): Promise<boolean> {
    const response = await HttpCotinService.Post<boolean>(`${CONTROLLER}/ArquivarMensagem`, { id });
    return Boolean(response);
}

export async function desarquivarMensagem(id: number): Promise<boolean> {
    const response = await HttpCotinService.Post<boolean>(`${CONTROLLER}/DesarquivarMensagem`, { id });
    return Boolean(response);
}

function mapUploadParaArquivoAnexo(api: Record<string, unknown>): ArquivoAnexo {
    const fileName = api.fileName ?? api.FileName;
    const rawDoc = api.documentID ?? api.DocumentID;
    let documentoID: number | undefined;
    if (typeof rawDoc === 'number' && rawDoc > 0) {
        documentoID = rawDoc;
    } else if (typeof rawDoc === 'string' && /^\d+$/.test(rawDoc.trim())) {
        const n = parseInt(rawDoc.trim(), 10);
        if (!Number.isNaN(n) && n > 0) {
            documentoID = n;
        }
    }
    const url = api.url ?? api.Url;
    const size = api.size ?? api.Size;
    const tamanho =
        typeof size === 'number'
            ? size
            : typeof size === 'string' && /^\d+$/.test(size.trim())
              ? parseInt(size.trim(), 10)
              : undefined;
    return {
        nomeArquivo: typeof fileName === 'string' ? fileName : '',
        documentoID,
        url: typeof url === 'string' ? url : undefined,
        tamanho,
    };
}

export async function uploadArquivoMensagem(file: File): Promise<ArquivoAnexo> {
    const arquivoBase64 = await fileToBase64(file);
    const request = {
        fileName: file.name,
        file: arquivoBase64,
        size: file.size,
    };
    const api = await HttpCotinService.Post<Record<string, unknown>>(`${CONTROLLER}/UploadArquivo`, request);
    return mapUploadParaArquivoAnexo(api);
}

export async function downloadArquivoMensagem(documentoID: number): Promise<Blob> {
    return HttpCotinService.GetBlob(`${CONTROLLER}/DownloadArquivo/${documentoID}`);
}
